// umlaut-check: skip — "zustaendig"/"zustaendigOptionen" sind Variablen-
// bzw. Query-Feldnamen (technische Bezeichner aus cosmos_vorschlaege),
// bleiben ASCII.

import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Alert, Container, Spinner} from "react-bootstrap";
import {usePage} from "@inertiajs/react";
import HeaderWithHelp from "../../Components/SalesAnalysis/HeaderWithHelp.jsx";
import PurposeBox from "../../Components/Cosmos/PurposeBox.jsx";
import SelectList from "../../Components/SelectList.jsx";
import VorschlaegeKacheln from "../../Components/Cosmos/VorschlaegeKacheln.jsx";
import VorschlaegeGruppe from "../../Components/Cosmos/VorschlaegeGruppe.jsx";
import VorschlaegeLieferwoche from "../../Components/Cosmos/VorschlaegeLieferwoche.jsx";
import AuffuellungModal from "../../Components/Cosmos/AuffuellungModal.jsx";
import useAnalysisData from "../../Components/SalesAnalysis/useAnalysisData.js";
import useGemerkteFilter, {AlleFilterZuruecksetzenLink, useGemerkteTabellenScroll} from "../../Components/Cosmos/useGemerkteFilter.jsx";
import {compareKw} from "../../Components/Cosmos/format.js";
import {hasAbility} from "../../Components/SalesAnalysis/format.js";

const PRODUKTLINIE_OPTIONEN = [
    {value: "", label: "Alle Produktlinien"},
    {value: "PREMIUM", label: "PREMIUM"},
    {value: "FLAME_ORANGE", label: "FLAME Orange"},
    {value: "FLAME_BLUE", label: "FLAME Blue"},
    {value: "BURNER", label: "BURNER"},
];

const DRINGLICHKEIT_OPTIONEN = [
    {value: "", label: "Jede Dringlichkeit"},
    {value: "ueberfaellig", label: "Überfällig"},
    {value: "sofort", label: "Sofort"},
    {value: "bald", label: "Bald"},
    {value: "geplant", label: "Geplant"},
];

const STATUS_OPTIONEN = [
    {value: "", label: "Jeder Status"},
    {value: "offen", label: "Offen"},
    {value: "uebernommen", label: "Übernommen"},
    {value: "verworfen", label: "Verworfen"},
];

/**
 * Bestellvorschläge je Kalenderwoche (AP9, PLAN.md Abschnitt 4.7): Kacheln,
 * Filterleiste, Wochen als aufklappbare Gruppen mit LKW-Auslastung und
 * Artikeltabelle. Der Vorschlag selbst löst nichts aus - keine Bestellung,
 * keine Mail, keine Weclapp-Übertragung; jede Statusänderung wird
 * gegengeprüft, nicht automatisch ausgeführt.
 */
// T2: "In erster Linie interessieren uns hier immer die Premium-Spraydosen" -
// Standard-Produktlinie dieser Seite ist PREMIUM statt "alle".
const STANDARD_FILTER = {
    produktlinie: "PREMIUM",
    dringlichkeit: "",
    status: "",
    zustaendig: "",
};

export default function CosmosVorschlaege() {
    const benutzerId = usePage().props.auth?.id;
    const {werte, setWert, zuruecksetzen, weichtAb} = useGemerkteFilter("vorschlaege", STANDARD_FILTER, {benutzerId});
    const {produktlinie, dringlichkeit, status, zustaendig} = werte;
    const [ladeSchluessel, setLadeSchluessel] = useState(0);
    const [angezeigt, setAngezeigt] = useState(null);
    const [auffuellungKw, setAuffuellungKw] = useState(null);

    const {data, loading, error} = useAnalysisData("/cosmos/daten/vorschlaege", {
        produktlinie: produktlinie || undefined,
        dringlichkeit: dringlichkeit || undefined,
        status: status || undefined,
        zustaendig: zustaendig || undefined,
        _r: ladeSchluessel || undefined,
    });

    useEffect(() => {
        if (data) {
            setAngezeigt(data);
        }
    }, [data]);

    // T2 Rücksprung vom Artikel-Blatt: Scroll-Position der Liste je Seite merken.
    const listeRef = useGemerkteTabellenScroll("vorschlaege", Boolean(angezeigt));

    const neuLaden = useCallback(() => setLadeSchluessel((n) => n + 1), []);
    const canOrder = hasAbility(angezeigt?.meta, "canOrder");
    const canArtikel = hasAbility(angezeigt?.meta, "canArtikel");

    const {data: benutzerDaten} = useAnalysisData("/cosmos/daten/vorschlaege/benutzer", {}, {skip: !canOrder});
    const benutzer = benutzerDaten?.benutzer || [];

    const zustaendigOptionen = useMemo(
        () => [{value: "", label: "Jede Zuständigkeit"}, {value: "keiner", label: "– niemand zugewiesen –"}, ...benutzer.map((b) => ({value: String(b.id), label: b.name}))],
        [benutzer]
    );

    // T16: Gruppierung nach Lieferwoche -> LKW -> Farbtöne, sobald der
    // Rechenkern das Feld "lieferwochen" liefert; bis dahin bleibt die
    // bisherige Gruppierung nach Bestellwoche erhalten (Rückfall).
    const lieferwochenSortiert = useMemo(
        () => (angezeigt?.lieferwochen ? [...angezeigt.lieferwochen].sort((a, b) => compareKw(a.kw, b.kw)) : null),
        [angezeigt]
    );
    const artikelNachId = useMemo(() => {
        const karte = new Map();
        for (const gruppe of angezeigt?.gruppen || []) {
            for (const zeile of gruppe.artikel || []) {
                karte.set(zeile.id, zeile);
            }
        }
        return karte;
    }, [angezeigt]);

    return (
        <Container fluid className="pt-2 pb-4">
            <PurposeBox storageKey="vorschlaege">
                Zeigt je Bestellwoche die fälligen Nachbestellungen mit vorgeschlagener Menge, Begründung,
                Dringlichkeit und LKW-Auslastung der Ankunftswoche. Jede Zeile hat einen Zuständigen und einen
                Status – offen, übernommen, verworfen oder überfällig.
            </PurposeBox>
            <div className="d-flex align-items-center gap-2 mb-2">
                <h1 className="h4 mb-0">
                    <HeaderWithHelp help="Fällige Nachbestellungen je Kalenderwoche, mit Begründung, LKW-Auslastung und Zuständigkeit.">
                        Bestellvorschläge
                    </HeaderWithHelp>
                </h1>
                {loading && angezeigt && <Spinner animation="border" size="sm" role="status" aria-label="Wird aktualisiert" />}
            </div>

            <Alert variant="info" className="py-2 small">
                Ein Vorschlag löst nichts aus – keine Bestellung, keine Mail, keine Übertragung an Weclapp. Jede
                Zeile wird gegengeprüft und erst durch „Übernehmen“ oder „Verwerfen“ entschieden.
            </Alert>

            {error && <p className="text-danger">{error}</p>}

            <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
                <div>
                    <HeaderWithHelp help="Schränkt die Liste auf eine der vier Produktlinien ein.">
                        <span className="small text-muted d-block mb-1">Produktlinie</span>
                    </HeaderWithHelp>
                    <SelectList value={produktlinie} options={PRODUKTLINIE_OPTIONEN} onChange={(wert) => setWert("produktlinie", wert)} size="sm" ariaLabel="Produktlinie" style={{minWidth: 190}} />
                </div>
                <div>
                    <HeaderWithHelp help="Schränkt die Liste auf eine Dringlichkeitsstufe ein – dieselbe Auswahl wie die Kacheln oben.">
                        <span className="small text-muted d-block mb-1">Dringlichkeit</span>
                    </HeaderWithHelp>
                    <SelectList value={dringlichkeit} options={DRINGLICHKEIT_OPTIONEN} onChange={(wert) => setWert("dringlichkeit", wert)} size="sm" ariaLabel="Dringlichkeit" style={{minWidth: 170}} />
                </div>
                <div>
                    <HeaderWithHelp help="Schränkt die Liste auf offene, übernommene oder verworfene Vorschläge ein.">
                        <span className="small text-muted d-block mb-1">Status</span>
                    </HeaderWithHelp>
                    <SelectList value={status} options={STATUS_OPTIONEN} onChange={(wert) => setWert("status", wert)} size="sm" ariaLabel="Status" style={{minWidth: 160}} />
                </div>
                <div>
                    <HeaderWithHelp help="Schränkt die Liste auf einen Zuständigen ein, oder auf noch nicht zugewiesene Vorschläge.">
                        <span className="small text-muted d-block mb-1">Zuständig</span>
                    </HeaderWithHelp>
                    <SelectList value={zustaendig} options={zustaendigOptionen} onChange={(wert) => setWert("zustaendig", wert)} size="sm" ariaLabel="Zuständig" style={{minWidth: 200}} />
                </div>
                <AlleFilterZuruecksetzenLink sichtbar={weichtAb} onClick={zuruecksetzen} />
            </div>

            {angezeigt && (
                <div ref={listeRef} style={{overflow: "auto"}}>
                    <VorschlaegeKacheln
                        kacheln={angezeigt.kacheln}
                        dringlichkeit={dringlichkeit}
                        status={status}
                        onSetDringlichkeit={(wert) => setWert("dringlichkeit", wert)}
                        onSetStatus={(wert) => setWert("status", wert)}
                    />

                    {/* T16: sobald der Rechenkern "lieferwochen" liefert, gruppiert
                        nach Lieferwoche -> LKW -> Farbtöne statt nach Bestellwoche. */}
                    {lieferwochenSortiert ? (
                        <>
                            {lieferwochenSortiert.length === 0 && (
                                <p className="text-muted">Keine Bestellvorschläge für die aktuelle Auswahl.</p>
                            )}
                            {lieferwochenSortiert.map((lieferwoche, index) => (
                                <VorschlaegeLieferwoche
                                    key={`${lieferwoche.kw}-${lieferwoche.zielort ?? ""}`}
                                    lieferwoche={lieferwoche}
                                    artikelNachId={artikelNachId}
                                    benutzer={benutzer}
                                    canOrder={canOrder}
                                    canArtikel={canArtikel}
                                    onZeileGeaendert={neuLaden}
                                    standardOffen={index === 0}
                                />
                            ))}
                        </>
                    ) : (
                        <>
                            {angezeigt.gruppen.length === 0 && (
                                <p className="text-muted">Keine Bestellvorschläge für die aktuelle Auswahl.</p>
                            )}
                            {angezeigt.gruppen.map((gruppe, index) => (
                                <VorschlaegeGruppe
                                    key={gruppe.kw ?? "dringend"}
                                    gruppe={gruppe}
                                    lkwAuslastung={angezeigt.lkw_auslastung}
                                    benutzer={benutzer}
                                    canOrder={canOrder}
                                    canArtikel={canArtikel}
                                    onAuffuellen={setAuffuellungKw}
                                    onZeileGeaendert={neuLaden}
                                    standardOffen={index === 0}
                                />
                            ))}
                        </>
                    )}
                </div>
            )}

            {!angezeigt && loading && (
                <div className="d-flex justify-content-center py-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Wird geladen…</span>
                    </Spinner>
                </div>
            )}

            <AuffuellungModal ankunftKw={auffuellungKw} onClose={() => setAuffuellungKw(null)} />
        </Container>
    );
}
